import type { ReactNode } from 'react';

/**
 * Semantic status chip following the Kinetic Monolith chip table
 * (`apps/ops-console/DESIGN.md` §2 "Semantic chips").
 *
 * Variants:
 *   - `ok`      — `surface-container-highest` bg, `primary` text
 *   - `warning` — `tertiary-container` bg, `on-tertiary-container` text
 *   - `error`   — `error-container` bg, `on-error-container` text
 *   - `neutral` — `surface-container-high` bg, `on-surface-variant` text
 */
type Variant = 'ok' | 'warning' | 'error' | 'neutral';

const VARIANT_CLASSES: Record<Variant, string> = {
  ok: 'bg-surface-container-highest text-primary',
  warning: 'bg-tertiary-container text-on-tertiary-container',
  error: 'bg-error-container text-on-error-container',
  neutral: 'bg-surface-container-high text-on-surface-variant',
};

interface Props {
  readonly variant: Variant;
  readonly label: ReactNode;
  readonly testId?: string;
}

export function StatusChip({ variant, label, testId }: Props): JSX.Element {
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] ${VARIANT_CLASSES[variant]}`}
    >
      {label}
    </span>
  );
}
